// Gunsmith kits: AWP (kind 'awp'), Pumpy (kind 'pumpy') and Gunker (kind 'dregs'). Numbers live in config.js WEAPONS.
//
// AWP      Hold fire to scope in (zoom, the cone tightens to nothing, a breath-hold settle at full scope), release to fire
//          ONE shot: always a full-damage hitscan line (one hit splats at any scope level - the scope only buys accuracy).
//          Then a long, visible, audible bolt cycle in which the gun can't fire. Costs a third of the tank.
// Pumpy    Press fire to shoot (a wide pellet cone), press again to pump the next shell in. Skip the pump and the gun stays
//          empty. The pump is slow enough that mashing is punished and steady rhythm is rewarded.
// Gunker   Auto-fire bead sprayer whose damage, bead size, speed, tightness and paint all scale with how EMPTY the tank is.
import * as THREE from 'three';
import { G, emit, clamp, lerp } from '../../core/ctx.js';
import { WEAPONS, PLAYER } from '../../config.js';
import { MAIN_KITS } from './registry.js';
import { CHARGES, LONG } from '../bots.js';
import './gunsmith-model.js';
import './gunsmith-sfx.js';

const AW = WEAPONS.awp, PW = WEAPONS.pumpy, DW = WEAPONS.dregs;
const _m = new THREE.Vector3(), _d = new THREE.Vector3(), _p = new THREE.Vector3();
const ease = (t) => t * t * (3 - 2 * t);

function rumble(a, strong, weak, ms) { if (a && a.isLocal && !a.isBot) G.input?.rumble?.(strong, weak, ms); }
function near(a) { return a.isLocal || a._nearCamera?.(); }
function snd(a, name, o = {}) { if (near(a)) G.audio?.play(name, { pos: a.isLocal ? undefined : a.pos, ...o }); }

// ================================================================================================ AWP
function awpKit(r) { return r.kit || (r.kit = { scope: 0, held: 0, bolt: 0, dinged: false, cancel: false, seen: 0, zoomWant: 0, seenT: 0, lastFight: -9, holdWant: 0, drift: 0 }); }

// Bots still scope by holding fire and let go to shoot (their AI drives it that way); players never charge:
// left click is one shot, right click only zooms.
function awpUpdateBot(runner, dt, inp, w) {
  const a = runner.a, k = awpKit(runner);
  if (inp.fire && k.bolt <= 0 && runner.cooldown <= 0) {
    if (!runner.charging) {
      if (a.ink < w.inkPerShot) { runner._empty(); return; }
      runner.charging = true; runner.charge = 0; k.scope = 0; k.held = 0; k.dinged = false; k.cancel = false; k.drift = 0;
      snd(a, 'awp_scope', { volume: a.isLocal ? 0.6 : 0.35 });
    }
    k.scope = Math.min(1, k.scope + dt / w.scopeTime);
    runner.charge = k.scope;
    a.fireFacing = 0.45;
    if (k.scope >= 1) {
      k.held += dt;
      if (!k.dinged) { k.dinged = true; snd(a, 'awp_breath', { volume: 0.5 }); }
    }
  } else if (runner.charging) {
    if (k.cancel && a.bot) awpCancel(runner, k);
    else awpFire(runner, w, k);
  }
  k.zoomWant = runner.charging ? w.scopeZoom * ease(k.scope) : 0;
}

function awpUpdate(runner, dt, inp, w) {
  const a = runner.a, k = awpKit(runner);
  k.seen = G.time;
  k.bolt = Math.max(0, k.bolt - dt);
  if (a.bot) return awpUpdateBot(runner, dt, inp, w);
  const ready = k.bolt <= 0 && runner.cooldown <= 0;
  // right click / LB: aim down the scope (zooms in and tightens the shot; nothing to charge)
  if (ready && inp.aim) {
    if (!runner.charging) {
      runner.charging = true; runner.charge = 0; k.scope = 0; k.held = 0; k.dinged = false;
      snd(a, 'awp_scope', { volume: a.isLocal ? 0.6 : 0.35 });
    }
    k.scope = Math.min(1, k.scope + dt / w.scopeTime);
    runner.charge = k.scope;
    a.fireFacing = 0.45;
    if (k.scope >= 1) {
      k.held += dt;
      if (!k.dinged) { k.dinged = true; snd(a, 'awp_breath', { volume: 0.5 }); rumble(a, 0.03, 0.2, 60); }
    }
  } else if (runner.charging) awpCancel(runner, k);   // lowered the scope
  // left click: one shot, scoped or from the hip (a hip shot sprays up to hipSpread degrees)
  if (ready && inp.firePressed) {
    if (a.ink < w.inkPerShot) runner._empty();
    else awpFire(runner, w, k);
  }
  k.zoomWant = runner.charging ? w.scopeZoom * ease(k.scope) : 0;
}

function awpCancel(runner, k) { runner.charging = false; runner.charge = 0; k.scope = 0; k.held = 0; k.cancel = false; k.zoomWant = 0; }

function awpFire(runner, w, k) {
  const a = runner.a;
  const sc = k.scope;
  awpCancel(runner, k);
  if (a.ink < w.inkPerShot) { runner._empty(); return; }
  a.ink -= w.inkPerShot; a.lastFire = 0;
  // one clean line: a hip shot sprays up to hipSpread degrees, a full scope is dead on
  G.projectiles.fireCharger(a, w, 1, w.hipSpread * Math.pow(1 - sc, 2));
  a.character.trigger('charge_release');
  runner.firingT = 0.6;
  runner.cooldown = w.boltTime; k.bolt = w.boltTime;
  // the bolt cycle is loud on purpose: everyone nearby hears the clacks and can see the bolt moving
  G.audio?.play('awp_bolt', { pos: a.isLocal ? undefined : a.pos, volume: a.isLocal ? 0.7 : 0.9, delay: 0.5 });
  if (a.isLocal) {
    emit('recoil', { amount: 0.045 });
    if (G.rig) G.rig.fovKick = (G.rig.fovKick || 0) - 7;   // a brief punch of the view
    rumble(a, 0.7, 0.55, 220);
  }
}

const awpBot = {
  long: true,
  paintPitch: -0.1,
  stayIn: () => false,                       // never brawl at low health: this gun can't win close fights
  targetBias(brain, e) {
    const a = brain.a, d = Math.hypot(e.pos.x - a.pos.x, e.pos.z - a.pos.z);
    let b = 0;
    if (d < AW.minRange) b -= 4;             // inside the minimum range: a bad target
    else if (d < AW.range) b += 0.8;
    if (e.vel && Math.hypot(e.vel.x, e.vel.z) < 1.5) b += 1.2;   // stationary enemies are free kills
    return b;
  },
  fight(brain, ctx) {
    const { a, w, dist, move } = ctx;
    const wr = a.weaponRunner, k = awpKit(wr);
    if (G.time - k.lastFight > 0.35) k.seenT = 0;     // lost sight: the visible-for-a-moment timer starts over
    k.seenT += ctx.dt; k.lastFight = G.time;
    if (k.bolt > 0) return false;
    const inBand = dist >= w.minRange && dist <= w.range * 1.05;
    if (!wr.charging) {
      if (!inBand || k.seenT < w.botSeen || wr.cooldown > 0) return false;
      k.holdWant = 0.05 + Math.random() * 0.4;
      return true;                                    // start scoping
    }
    if (!inBand) { k.cancel = true; return false; }
    move.set(0, 0, 0);                                // scope in only when stationary
    if (k.scope < 1) return true;
    return k.held < k.holdWant;                       // loose after the breath-hold
  },
  tactics(brain, ctx) {
    const { a, w, dist, it, move, target, retreat, aimed, dt } = ctx;
    const wr = a.weaponRunner, k = awpKit(wr);
    if (retreat) { if (wr.charging) k.cancel = true; return; }
    if (dist < w.minRange) {                           // an enemy closed in: drop the scope and back off
      if (wr.charging) k.cancel = true;
      it.fire = false;
      const dx = a.pos.x - target.pos.x, dz = a.pos.z - target.pos.z, l = Math.hypot(dx, dz) || 1;
      move.set(dx / l, 0, dz / l);
      return;
    }
    if (wr.charging) {
      move.set(0, 0, 0);
      if (!aimed) { k.drift += dt; it.fire = k.drift < 1.1; if (!it.fire) k.cancel = true; } else k.drift = 0;   // keep the breath while re-aiming, give up if it won't settle
    }
  },
  paint(brain, ctx) {
    const { a, needPaint, inkFrac } = ctx;
    const wr = a.weaponRunner, k = awpKit(wr);
    if (wr.charging) return false;                     // a painting bot taps off a hip shot
    if (k.bolt > 0 || wr.cooldown > 0) return false;
    if (!(needPaint && inkFrac > 0.75 && (brain.paintPause || 0) <= 0)) return false;
    brain.paintPause = 2 + Math.random() * 2;
    return true;
  },
};
CHARGES.awp = true;
LONG.awp = true;

// ================================================================================================ Pumpy
function pumpyKit(r) { return r.kit || (r.kit = { chambered: true, pumping: false, pumpT: 0, pumpAge: 99, pumpDur: PW.pumpTime, buf: 0, prevFire: false, botT: 0, seen: 0 }); }

function pumpyUpdate(runner, dt, inp, w) {
  const a = runner.a, k = pumpyKit(runner);
  k.seen = G.time; k.pumpDur = w.pumpTime;
  if (k.pumpAge < 99) k.pumpAge += dt;
  if (k.pumping) {
    k.pumpT -= dt;
    if (k.pumpT <= 0) { k.pumping = false; k.chambered = true; snd(a, 'pumpy_chamber', { volume: 0.5 }); }
  }
  const edge = inp.fire && !k.prevFire; k.prevFire = !!inp.fire;
  if (edge) k.buf = 0.28; else k.buf = Math.max(0, k.buf - dt);
  let press = k.buf > 0;
  if (a.isBot) { k.botT -= dt; press = !!inp.fire && k.botT <= 0; }
  if (!press || runner.cooldown > 0 || k.pumping) return;
  k.buf = 0;
  if (a.isBot) k.botT = 0.04 + Math.random() * 0.14;
  if (k.chambered) {
    if (a.ink < w.inkPerShot) { runner._empty(); return; }
    k.chambered = false;
    a.ink -= w.inkPerShot; a.lastFire = 0;
    runner.firingT = 0.45; a.fireFacing = 0.5;
    pumpyFire(a, w);
    a.character.trigger('shoot');
    runner.cooldown = w.fireInterval;
    k.pumpAge = 99;
  } else {
    // rack the next shell: slow enough that mashing the trigger wastes time, quick enough for a steady rhythm
    k.pumping = true; k.pumpT = w.pumpTime; k.pumpAge = 0;
    runner.firingT = 0.4; a.fireFacing = 0.4;
    snd(a, 'pumpy_pump', { volume: a.isLocal ? 0.7 : 0.55 });
    runner.cooldown = 0;
  }
}

function pumpyFire(a, w) {
  const PJ = G.projectiles;
  const m = PJ._muzzle(a, new THREE.Vector3()), dir = PJ._aimFrom(a, m, new THREE.Vector3());
  const spread = a.grounded ? w.spreadDeg : w.spreadAir;
  for (let i = 0; i < w.pellets; i++) {
    _d.copy(dir);
    if (i > 0) PJ._spread(_d, spread);
    const p = PJ._new();
    Object.assign(p, {
      type: 'drop', weaponId: 'pumpy', wid: 'pumpy', owner: a, team: a.team, age: 0, life: w.pelletLife, straight: 0.09, radius: w.pelletPaint * (0.8 + Math.random() * 0.4),
      damage: w.pelletDamage, dmgFar: w.pelletDamageFar, size: w.pelletSize, trail: 0, trailEvery: 0, trailRadius: 0.3, grav: w.pelletGrav, drag: w.pelletDrag, seed: Math.random(),
      vis: w.pelletSize * 0.8, tail0: 0.5, tailK: 1.0, wob: 0.05, wobF: 22, nose: 0.1, sats: 1,
    });
    p.pos.copy(m); p.prev.copy(m); p.start.copy(m);
    p.vel.copy(_d).multiplyScalar(w.projSpeed * (0.93 + Math.random() * 0.14));
    PJ._push(p);
  }
  if (near(a)) { G.audio?.play('shoot_pumpy', { pos: a.isLocal ? undefined : m, volume: a.isLocal ? 0.9 : 0.65 }); G.fx?.muzzle(m, dir, a.color, 'blaster'); }
  if (a.isLocal) { emit('recoil', { amount: 0.028 }); rumble(a, 0.5, 0.5, 120); }
  emit('weapon:fire', { actor: a, weapon: w.id, muzzle: m.clone(), dir: dir.clone() });
}

const pumpyBot = {
  paintPitch: -0.4,
  fight(brain, ctx) { const { dist } = ctx; return dist < ctx.w.range * 1.2; },
  paint(brain, ctx) { return ctx.needPaint && ctx.inkFrac > 0.25; },
};

// ================================================================================================ Gunker
// power 0 (full tank) → 1 (empty): everything scales on it
const dPower = (a) => Math.pow(clamp(1 - a.ink / PLAYER.inkMax, 0, 1), 1.25);
function dregsUpdate(runner, dt, inp, w) {
  const a = runner.a;
  if (!inp.fire) { if (runner.cooldown < 0) runner.cooldown = 0; return; }
  runner.firingT = 0.35; a.fireFacing = 0.5;
  let g = 0;
  while (runner.cooldown <= 0 && g++ < 3) {
    if (a.ink < w.inkPerShot) { runner._empty(); runner.cooldown += w.fireInterval; break; }
    const pw = dPower(a);
    a.ink -= w.inkPerShot; a.lastFire = 0;
    dregsFire(a, w, pw);
    a.character.trigger('shoot');
    runner.cooldown += w.fireInterval;
  }
}
function dregsFire(a, w, pw) {
  const PJ = G.projectiles;
  const m = PJ._muzzle(a, new THREE.Vector3()), dir = PJ._aimFrom(a, m, new THREE.Vector3());
  const speed = lerp(w.speedMin, w.speedMax, pw), straight = lerp(0.07, 0.2, pw), range = lerp(w.rangeMin, w.rangeMax, pw);
  PJ._ballistic(m, dir, a.aimPoint, speed, straight, 28, 0.8, range);
  PJ._spread(dir, (a.grounded ? lerp(w.spreadFull, w.spreadEmpty, pw) : lerp(w.spreadFull, w.spreadEmpty, pw) * 1.9));
  const size = lerp(w.sizeMin, w.sizeMax, pw);
  PJ.fireCustom(a, m, dir, {
    type: 'shot', speed, damage: lerp(w.damageMin, w.damageMax, pw), straight, range, radius: lerp(w.paintMin, w.paintMax, pw), size,
    trailEvery: 1.1, trailRadius: lerp(0.28, 0.55, pw), weaponId: 'dregs',
    look: { vis: size * 0.7, tail0: 0.7, tailK: 1.2, wob: 0.045, wobF: 22, nose: 0.25, sats: pw > 0.5 ? 3 : 2 },
  });
  if (near(a)) { G.audio?.play('shoot_dregs', { pos: a.isLocal ? undefined : m, volume: a.isLocal ? 0.6 : 0.4, pitch: 0.85 + 0.5 * pw }); G.fx?.muzzle(m, dir, a.color, 'shooter'); }
  if (a.isLocal && a.weaponRunner.rumbleT <= 0) { a.weaponRunner.rumbleT = 0.09; rumble(a, 0.02 + 0.06 * pw, 0.1, 40); }
  emit('weapon:fire', { actor: a, weapon: w.id, muzzle: m.clone(), dir: dir.clone() });
}
const dregsBot = {
  fight(brain, ctx) { const pw = dPower(ctx.a); return ctx.dist < lerp(DW.rangeMin, DW.rangeMax, pw) * 1.05; },
  stayIn: (brain) => dPower(brain.a) > 0.6,           // nearly empty = at its strongest: finish the fight
};

// ================================================================================================ registration
function hangCheck(kind, drop) {
  // a scope / hold left hanging (a special took the trigger mid-hold, so the runner stopped updating): let it go quietly
  for (const a of G.actors) {
    const r = a.weaponRunner;
    if (r && r.kit && a.weapon?.kind === kind && G.time - (r.kit.seen ?? G.time) > 0.15) drop(r);
  }
}
MAIN_KITS.awp = {
  update: awpUpdate,
  reset(runner) { runner.kit = null; },
  busy: (r) => !!(r.kit && r.kit.scope > 0),   // the bolt cycle keeps ticking while morphed, so it never blocks the morph
  firingPose: (r) => !!(r.kit && (r.kit.scope > 0 || r.kit.bolt > 0.9)),
  moveSpeed(r, w) {
    const k = r.kit;
    if (!k) return 0;
    if (r.charging) return lerp(PLAYER.runSpeed * 0.8, w.moveSpeedScoped, ease(k.scope));
    if (k.bolt > 0) return w.moveSpeedFiring;
    return 0;
  },
  spreadDeg: (r, w) => (r.charging && r.kit ? w.hipSpread * Math.pow(1 - r.kit.scope, 2) : w.hipSpread),
  tick() { hangCheck('awp', (r) => { if (r.charging) { r.charging = false; r.charge = 0; r.kit.scope = 0; r.kit.zoomWant = 0; } }); },
  bot: awpBot,
};
MAIN_KITS.pumpy = {
  update: pumpyUpdate,
  reset(runner) { runner.kit = null; },
  busy: () => false,   // morph straight out of a shot or mid-pump (the rack finishes while morphed)
  firingPose: (r) => !!(r.kit && r.kit.pumping),
  moveSpeed(r, w) { const k = r.kit; if (k && k.pumping) return w.moveSpeedPump; return r.firingT > 0 ? w.moveSpeedFiring : 0; },
  spreadDeg: (r, w) => (r.a.grounded ? w.spreadDeg : w.spreadAir),
  bot: pumpyBot,
};
MAIN_KITS.dregs = {
  update: dregsUpdate,
  reset(runner) { runner.kit = null; },
  spreadDeg: (r, w) => { const pw = dPower(r.a); return lerp(w.spreadFull, w.spreadEmpty, pw) * (r.a.grounded ? 1 : 1.9); },
  bot: dregsBot,
};
export const GUNSMITH_DEBUG = { dPower, awpKit, pumpyKit };
